import React, { useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Button,
  Select,
  Input,
  Switch,
  Tag,
  Space,
  Modal,
  message,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { surveyCode } from '../../Config/surveyCode';
import { getSubTypeLabel } from '../../Config/subType';
import config from '../../Config/key';
// テーブル強化 PJ: 他一覧と同じく列リサイズ + ソート + 省略を適用
import {
  PageContainer,
  PageHeader,
  SectionCard,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
} from '../../common';
import QSetDetailModal from './QSetDetailModal';

/**
 * QuestionSetMgrPage — 設問セット管理画面
 * ------------------------------------------------------------------
 * 機能企画書 F-02, F-03, F-04, F-05, F-06 に対応する管理画面。
 *
 * 機能:
 *   - 一覧表示 (filter: survey_code, type_360, set_name 검색, 사용불가 포함/제외)
 *   - 各行操作: 상세 보기 (F-04), 편집 (F-02), 사용불가 토글 (F-03), CSV ダウンロード (F-05)
 *   - 사용불가 토글 시 사용 중 시즌 수 확인 후 警告 다이얼로그 (F-06)
 *   - 좌상단 「新規登録」버튼 → MakeQSetPage 로 遷移
 *
 * 認証·権限:
 *   localStorage の w_auth トークンを操作系 API に送信。
 *   API 側で role 9 限定でガード。
 */

const TYPE_360_OPTIONS = [
  { value: '6x6', label: '6x6' },
  { value: '6x5', label: '6x5' },
  { value: '5x6', label: '5x6' },
  { value: 'cup', label: 'cup' },
  { value: '3x3x5', label: '3x3x5' },
  { value: 'gates', label: 'gates' },
  { value: 'eleader', label: 'eleader' },
  { value: 'initiative', label: 'initiative' },
];

const QuestionSetMgrPage = () => {
  const history = useHistory();

  // ----- 一覧データ -----
  const [allSets, setAllSets] = useState([]);
  const [seasonCountMap, setSeasonCountMap] = useState({});
  const [loading, setLoading] = useState(false);

  // ----- フィルタ状態 -----
  const [filterSurveyCode, setFilterSurveyCode] = useState(null);
  const [filterType360, setFilterType360] = useState(null);
  const [filterKeyword, setFilterKeyword] = useState('');
  const [includeDisabled, setIncludeDisabled] = useState(true);

  // ----- 詳細モーダル状態 -----
  const [detailModalSetId, setDetailModalSetId] = useState(null);

  // -------------------------------------------------------------------
  //  一覧読み込み
  // -------------------------------------------------------------------
  const loadSets = async () => {
    setLoading(true);
    try {
      const res = await Axios.get('/api/questionSet/');
      if (res.data.success) {
        const sets = res.data.questionSet || [];
        setAllSets(sets);
        // 各セットの使用中シーズン数を並列取得 (F-06)
        const counts = await Promise.all(
          sets.map(async (s) => {
            try {
              const r = await Axios.get(`/api/questionSet/${s._id}/seasons`);
              return [s._id, r.data.count || 0];
            } catch {
              return [s._id, 0];
            }
          })
        );
        setSeasonCountMap(Object.fromEntries(counts));
      }
    } catch (error) {
      console.log(error);
      message.error('設問セット一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSets();
  }, []);

  // -------------------------------------------------------------------
  //  フィルタ済みリスト
  // -------------------------------------------------------------------
  const filteredSets = useMemo(() => {
    const filtered = allSets.filter((s) => {
      if (filterSurveyCode && s.survey_code !== filterSurveyCode) return false;
      if (filterType360 && s.type_360 !== filterType360) return false;
      if (filterKeyword) {
        // セット名列の表示（セット名（説明））と同じ範囲を検索対象にする
        const target = `${s.set_name || ''} ${s.set_description || ''}`.toLowerCase();
        if (!target.includes(filterKeyword.toLowerCase())) return false;
      }
      if (!includeDisabled && s.disabled) return false;
      return true;
    });
    // 新しく登録されたセットを上に表示 (createdAt 降順)
    // createdAt がない古いデータは _id (ObjectId) 降順でフォールバック
    return filtered.slice().sort((a, b) => {
      const aT = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bT = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (bT !== aT) return bT - aT;
      return String(b._id || '').localeCompare(String(a._id || ''));
    });
  }, [allSets, filterSurveyCode, filterType360, filterKeyword, includeDisabled]);

  // フィルタ用 survey_code 候補 (登録済みのみ)
  const surveyCodeOptions = useMemo(() => {
    const codes = Array.from(new Set(allSets.map((s) => s.survey_code))).filter(
      Boolean
    );
    return codes.map((c) => ({
      value: c,
      label: `${c} - ${surveyCode[c] || ''}`,
    }));
  }, [allSets]);

  // -------------------------------------------------------------------
  //  사용불가 トグル (F-03, F-06)
  //  使用中シーズンが 1個以上ある場合は警告 dialog
  // -------------------------------------------------------------------
  const toggleDisabled = async (record, nextDisabled) => {
    const seasonCount = seasonCountMap[record._id] || 0;

    const exec = async () => {
      try {
        const res = await Axios.patch(
          `/api/questionSet/${record._id}/disabled`,
          {
            disabled: nextDisabled,
            executorToken: localStorage.getItem('w_auth'),
          }
        );
        if (res.data.success) {
          message.success(
            nextDisabled
              ? '使用不可に設定しました'
              : '使用可に戻しました'
          );
          // ローカル状態も更新
          setAllSets((prev) =>
            prev.map((s) =>
              s._id === record._id ? { ...s, disabled: nextDisabled } : s
            )
          );
        } else {
          message.error(res.data.error || '更新に失敗しました');
        }
      } catch (error) {
        const msg = error.response?.data?.error || error.message;
        message.error(`更新エラー: ${msg}`);
      }
    };

    // F-06: 使用中シーズンがある状態で OFF→ON (使用不可化) する時のみ警告
    if (nextDisabled && seasonCount > 0) {
      Modal.confirm({
        title: '使用不可にしますか?',
        content: `このセットは現在 ${seasonCount} 個のシーズンで使用中です。それでも使用不可に設定しますか?`,
        okText: '使用不可にする',
        okType: 'danger',
        cancelText: 'キャンセル',
        onOk: exec,
      });
    } else {
      exec();
    }
  };

  // -------------------------------------------------------------------
  //  CSV ダウンロード (F-05)
  // -------------------------------------------------------------------
  const handleExport = (record) => {
    // window.open は axios インターセプターを経由しないので、
    // API キーとユーザートークン両方をクエリパラメータで送る必要がある
    // (apiKey ミドルウェアは req.query.apiKey も許可している)
    const token = encodeURIComponent(localStorage.getItem('w_auth') || '');
    const apiKey = encodeURIComponent(config.API_KEY || '');
    const url = `/api/questionSet/${record._id}/export?token=${token}&apiKey=${apiKey}`;
    window.open(url, '_blank');
  };

  // -------------------------------------------------------------------
  //  詳細表示 (F-04) - QSetDetailModal を開く
  // -------------------------------------------------------------------
  const handleShowDetail = (record) => {
    setDetailModalSetId(record._id);
  };

  // -------------------------------------------------------------------
  //  編集画面遷移 (F-02, F-04)
  // -------------------------------------------------------------------
  const handleEdit = (record) => {
    history.push(`/editqset/${record._id}`);
  };

  // -------------------------------------------------------------------
  //  Table column 定義
  // -------------------------------------------------------------------
  const rawColumns = [
    ellipsisColumn({
      title: 'セット名',
      dataIndex: 'set_name',
      key: 'set_name',
      width: 420,
      sorter: textSorter('set_name'),
      render: (text, record) => (
        <Space>
          <a onClick={() => handleShowDetail(record)}>
            {text}
            {record.set_description ? `（${record.set_description}）` : ''}
          </a>
          {record.disabled && <Tag color="red">※使用不可</Tag>}
        </Space>
      ),
    }),
    {
      title: '形式',
      dataIndex: 'type_360',
      key: 'type_360',
      width: 90,
      sorter: textSorter('type_360'),
      render: (v) => v && <Tag color="orange">{v}</Tag>,
    },
    {
      title: '区分',
      dataIndex: 'sub_type',
      key: 'sub_type',
      width: 130,
      sorter: textSorter('sub_type'),
      render: (v) => getSubTypeLabel(v),
    },
    {
      title: '使用中シーズン',
      key: 'seasonCount',
      width: 140,
      sorter: (a, b) =>
        (seasonCountMap[a._id] || 0) - (seasonCountMap[b._id] || 0),
      render: (_, record) => {
        const count = seasonCountMap[record._id] || 0;
        return count > 0 ? (
          <Tag color="blue">{count} 個シーズン</Tag>
        ) : (
          <Tag color="default">未使用</Tag>
        );
      },
    },
    {
      title: '使用不可',
      key: 'disabled',
      width: 100,
      render: (_, record) => (
        <Switch
          checked={!!record.disabled}
          onChange={(checked) => toggleDisabled(record, checked)}
        />
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      resizable: false,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="詳細">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleShowDetail(record)}
            />
          </Tooltip>
          <Tooltip title="編集">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="CSV ダウンロード">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleExport(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 列幅ドラッグ・初期フィット・横スクロールは共用 hook が配線
  const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);

  return (
    <PageContainer fluid>
      <PageHeader
        title="設問セット管理"
        subtitle="登録済み設問セットの一覧·編集·使用不可設定·CSV ダウンロードを行います"
        divider
      />

      {/* --- フィルタ --- */}
      <SectionCard
        title="絞り込み"
        style={{ marginBottom: 'var(--space-6)' }}
      >
        <Space wrap size="middle">
          <Select
            allowClear
            placeholder="サーベイコード"
            style={{ width: 240 }}
            value={filterSurveyCode}
            onChange={setFilterSurveyCode}
            options={surveyCodeOptions}
          />
          <Select
            allowClear
            placeholder="形式 (type_360)"
            style={{ width: 160 }}
            value={filterType360}
            onChange={setFilterType360}
            options={TYPE_360_OPTIONS}
          />
          <Input
            placeholder="セット名・説明で検索"
            prefix={<SearchOutlined />}
            style={{ width: 220 }}
            value={filterKeyword}
            onChange={(e) => setFilterKeyword(e.target.value)}
            allowClear
          />
          <Space size={4}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--grey-65)' }}>
              使用不可を含める
            </span>
            <Switch checked={includeDisabled} onChange={setIncludeDisabled} />
          </Space>
        </Space>
      </SectionCard>

      {/* --- 一覧 Table --- */}
      <SectionCard>
        <div ref={wrapRef}>
          <Table
            rowKey="_id"
            loading={loading}
            dataSource={filteredSets}
            columns={columns}
            components={components}
            scroll={scroll}
            size="middle"
            pagination={{
              defaultPageSize: 100,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} / ${total}件`,
            }}
            rowClassName={(record) =>
              record.disabled ? 'qset-row-disabled' : ''
            }
          />
        </div>
        {/* 既存 SeasonList と同じく、テーブル直下·左寄せに新規登録ボタン */}
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => history.push('/makeqset')}
          >
            新規登録
          </Button>
        </div>
      </SectionCard>

      {/* 使用不可行を薄く見せる軽い CSS */}
      <style>{`
        .qset-row-disabled td {
          color: var(--grey-45);
          background: var(--grey-10);
        }
      `}</style>

      {/* 詳細モーダル (F-04, F-08) */}
      <QSetDetailModal
        visible={!!detailModalSetId}
        onClose={() => setDetailModalSetId(null)}
        questionSetId={detailModalSetId}
        allSets={allSets}
        onEdit={(set) => {
          setDetailModalSetId(null);
          history.push(`/editqset/${set._id}`);
        }}
        onExport={(set) => handleExport(set)}
      />
    </PageContainer>
  );
};

export default QuestionSetMgrPage;
